import React, { useState } from 'react';
import {
    Card, Row, Col, Form, Input, Select,
    Button, Tag, Avatar, Modal, Divider, Badge, Space, Empty
} from 'antd';
import { TIP_INCONNU } from './declarationTips';
import {
    UserOutlined, CarOutlined, PlusOutlined, DeleteOutlined,
    MedicineBoxOutlined, RobotOutlined
} from '@ant-design/icons';
import { SINISTER_COLORS } from '../../../../components/ui/colors';

const { Option } = Select;
const { TextArea } = Input;

/**
 * Construit la liste des véhicules disponibles pour l'affectation d'une victime :
 *  - le véhicule assuré
 *  - les véhicules des adversaires
 *  - aucun (piéton)
 */
const buildVehicleOptions = (contrat, adversaires) => {
    const opts = [];

    // Véhicule assuré
    if (contrat) {
        const label = contrat.immatriculation
            ? `${contrat.immatriculation} – Véhicule assuré`
            : 'Véhicule assuré';
        opts.push({ value: 'ASSURE', label });
    }

    // Véhicules adversaires
    adversaires.forEach((adv, i) => {
        if (adv.typeAdversaire?.startsWith('VEHICULE')) {
            const immat = adv.immatriculation || `Adversaire ${i + 1}`;
            const nom = adv.nom ? ` – ${adv.nom} ${adv.prenom || ''}` : '';
            opts.push({ value: `ADV_${adv.id}`, label: `${immat}${nom}` });
        }
    });

    // Aucun (piéton)
    opts.push({ value: 'AUCUN', label: '🚶 Aucun (piéton / hors véhicule)' });

    return opts;
};

/**
 * Génère automatiquement les victimes à partir :
 *  - du conducteur assuré (si dommage corporel/mixte)
 *  - des conducteurs adversaires (si dommage corporel/mixte)
 */
export const buildAutoVictimes = (formData, isAssureConducteur, adversaires, contrat) => {
    console.log('buildAutoVictimes', { formData, isAssureConducteur, adversaires, contrat });
    const auto = [];

    // ── Conducteur du véhicule assuré ──
    const typeDommageAssure = formData?.typeDommageAssure;
    if (typeDommageAssure === 'CORPOREL' || typeDommageAssure === 'MIXTE') {
        const conducteur = isAssureConducteur
            ? { nom: contrat?.nom || '', prenom: '', source: 'Assuré conducteur' }
            : { nom: formData?.conducteur?.nom || '', prenom: formData?.conducteur?.prenom || '', source: 'Conducteur' };

        auto.push({
            id: `auto_assure_conducteur`,
            auto: true,
            nom: conducteur.nom,
            prenom: conducteur.prenom,
            source: conducteur.source,
            vehiculeAffecte: 'ASSURE',
            vehiculeLabel: 'Véhicule assuré',
            typeDommage: typeDommageAssure,
            lesions: formData?.conducteurDommages?.lesions || '',
            etat: formData?.conducteurDommages?.etat || '',
        });
    }

    // ── Conducteurs adversaires ──
    adversaires.forEach((adv) => {
        if (adv.typeSinistre === 'CORPOREL' || adv.typeSinistre === 'MIXTE') {
            auto.push({
                id: `auto_adv_${adv.id}`,
                auto: true,
                nom: adv.nom || '',
                prenom: adv.prenom || '',
                source: `Conducteur adversaire – ${adv.immatriculation || adv.typeAdversaire}`,
                vehiculeAffecte: `ADV_${adv.id}`,
                vehiculeLabel: adv.immatriculation || `Adversaire`,
                typeDommage: adv.typeSinistre,
                lesions: adv.descriptionDegatsCorporels || '',
            });
        }
    });

    return auto;
};

// ────────────────────────────────────────────────────────────────────────────
// Carte individuelle d'une victime
// ────────────────────────────────────────────────────────────────────────────
const VictimeCard = ({ victime, onDelete, vehicleOptions }) => {
    const vehicleLabel = vehicleOptions.find(v => v.value === victime.vehiculeAffecte)?.label || victime.vehiculeLabel || '—';

    return (
        <Card
            size="small"
            style={{
                height: '100%',
                borderLeft: victime.auto
                    ? `4px solid ${SINISTER_COLORS.primary}`
                    : '4px solid #52c41a',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                opacity: victime.auto ? 0.92 : 1,
            }}
            actions={victime.auto ? [] : [
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onDelete(victime.id)}>
                    Retirer
                </Button>
            ]}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <Avatar
                    icon={victime.auto ? <RobotOutlined /> : <UserOutlined />}
                    style={{
                        background: victime.auto
                            ? `linear-gradient(135deg, ${SINISTER_COLORS.primary} 0%, #764ba2 100%)`
                            : '#52c41a'
                    }}
                />
                <div>
                    <div style={{ fontWeight: 'bold' }}>
                        {victime.nom || '–'} {victime.prenom}
                    </div>
                    {victime.auto && (
                        <Tag color="purple" style={{ fontSize: 10 }}>
                            🤖 Auto — {victime.source}
                        </Tag>
                    )}
                </div>
            </div>

            <div style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {victime.vehiculeAffecte && (
                    <div>
                        <CarOutlined style={{ marginRight: 4 }} />
                        {vehicleLabel}
                    </div>
                )}
                {victime.typeDommage && (
                    <Tag color={victime.typeDommage === 'MIXTE' ? 'orange' : 'red'} style={{ width: 'fit-content' }}>
                        {victime.typeDommage}
                    </Tag>
                )}
                {victime.telephone && <div>📞 {victime.telephone}</div>}
                {victime.profession && <div>💼 {victime.profession}</div>}
                {victime.centreEvacuation && (
                    <div>🏥 {victime.centreEvacuation}</div>
                )}
                {victime.lesions && (
                    <div style={{ marginTop: 4, color: '#c62828', fontStyle: 'italic' }}>
                        🩹 {victime.lesions.length > 70 ? victime.lesions.substring(0, 70) + '…' : victime.lesions}
                    </div>
                )}
            </div>
        </Card>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// Composant principal VictimesSection
// ────────────────────────────────────────────────────────────────────────────
const VictimesSection = ({
    victimes,
    setVictimes,
    autoVictimes,
    professions,
    contrat,
    adversaires,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [victimeForm] = Form.useForm();

    const vehicleOptions = buildVehicleOptions(contrat, adversaires);

    const handleAjouter = async () => {
        try {
            const values = await victimeForm.validateFields();
            setVictimes([...victimes, { ...values, id: Date.now(), auto: false }]);
            victimeForm.resetFields();
            setShowModal(false);
        } catch (e) {/* validation */ }
    };

    // Toutes les victimes = auto + manuelles
    const allVictimes = [...autoVictimes, ...victimes];

    return (
        <>
            <div style={{ marginBottom: 16 }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                            👥 Liste des victimes
                        </h4>
                        <p style={{ color: '#666', fontSize: 12, margin: '4px 0 0 0' }}>
                            Les victimes marquées <Tag color="purple" style={{ fontSize: 10 }}>🤖 Auto</Tag> sont ajoutées automatiquement selon les dommages renseignés.
                        </p>
                    </Col>
                    <Col>
                        <Badge count={victimes.length} style={{ backgroundColor: '#52c41a' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setShowModal(true)}
                                style={{ background: SINISTER_COLORS.primary, border: 'none' }}
                            >
                                Ajouter une victime
                            </Button>
                        </Badge>
                    </Col>
                </Row>
            </div>

            {allVictimes.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Aucune victime pour l'instant. Les victimes auto-détectées apparaîtront ici selon les types de dommages renseignés."
                />
            ) : (
                <Row gutter={[16, 16]}>
                    {allVictimes.map((v) => (
                        <Col xs={24} sm={12} md={8} key={v.id}>
                            <VictimeCard
                                victime={v}
                                vehicleOptions={vehicleOptions}
                                onDelete={(id) => setVictimes(victimes.filter(x => x.id !== id))}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            {/* ─── Modal ajout victime ─── */}
            <Modal
                title={<Space><MedicineBoxOutlined /> Ajouter une victime</Space>}
                open={showModal}
                onCancel={() => { setShowModal(false); victimeForm.resetFields(); }}
                onOk={handleAjouter}
                okText="Ajouter"
                cancelText="Annuler"
                width={700}
                maskClosable={false}
            >
                <Form form={victimeForm} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Nom" name="nom" tooltip={TIP_INCONNU}
                                rules={[{ required: true, message: 'Nom requis' }]}>
                                <Input placeholder="Nom (ou INCONNU)" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Prénom" name="prenom" tooltip={TIP_INCONNU}
                                rules={[{ required: true, message: 'Prénom requis' }]}>
                                <Input placeholder="Prénom (ou INCONNU)" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Téléphone" name="telephone">
                                <Input placeholder="Téléphone" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Profession" name="profession">
                                <Select options={professions} placeholder="Sélectionner" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Adresse" name="adresse">
                        <Input placeholder="Adresse" />
                    </Form.Item>

                    {/* ── Véhicule d'affectation ── */}
                    <Form.Item
                        label={
                            <span style={{ fontWeight: 600 }}>
                                <CarOutlined style={{ marginRight: 6 }} />
                                Véhicule dans lequel se trouvait la victime
                            </span>
                        }
                        name="vehiculeAffecte"
                        rules={[{ required: true, message: 'Veuillez sélectionner un véhicule ou choisir Aucun' }]}
                    >
                        <Select placeholder="Sélectionner le véhicule ou Aucun (piéton)" options={vehicleOptions} style={{ width: '100%' }} />
                    </Form.Item>

                    <Divider orientation="left" style={{ color: '#c62828', borderColor: '#f5576c' }}>
                        <MedicineBoxOutlined /> Informations médicales
                    </Divider>

                    <Form.Item
                        label="Type de dommage"
                        name="typeDommage"
                    >
                        <Select placeholder="Type de dommage subi" style={{ width: '100%' }}>
                            <Option value="CORPOREL">🏥 Corporel</Option>
                            <Option value="MIXTE">⚠️ Mixte</Option>
                            <Option value="MATERIEL">🔧 Matériel</Option>
                            <Option value="AUCUN">✅ Aucun</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Centre de soins / Structure d'évacuation"
                        name="centreEvacuation"
                    >
                        <Input placeholder="Ex: CHU Sylvanus Olympio, Clinique X…" />
                    </Form.Item>

                    <Form.Item
                        label="Lésions / Dommages corporels"
                        name="lesions"
                    >
                        <TextArea rows={3} placeholder="Décrivez les lésions constatées…" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default VictimesSection;